// Side-attached Done tab — fixed to the LEFT viewport edge. Mirror of
// BacklogPeekTab: toggles the Done rail and acts as a drop target for project
// cards dragged off the Grid. When a card is in flight the tab widens and
// shows a "drop to mark done" affordance so the interaction is discoverable.
//
// Portaled to body so it shares the rail's outer stacking context and
// IonRouterOutlet's transform doesn't trap it.

import { createPortal } from 'react-dom';

import { IconCheckCircle, IconChev } from './Icons';
import { usePmCanvasLeft } from './usePmCanvasLeft';

interface Props {
  count: number;
  open: boolean;
  onToggle: () => void;
  /** True while a project card is being dragged — drives the widened
   *  "drop here to mark done" affordance. */
  dragActive: boolean;
  /** True while a drag is hovering this tab. Highlights the drop zone. */
  dragOverHere: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

const DonePeekTab: React.FC<Props> = ({
  count,
  open,
  onToggle,
  dragActive,
  dragOverHere,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  const canvasLeft = usePmCanvasLeft();
  return createPortal(
    <div className="pm-app pm-peek-portal">
    <button
      type="button"
      // Sit at the grid's left edge, not the viewport's — clear of the navs.
      style={{ left: canvasLeft }}
      className={[
        'pm-done-peek',
        open ? 'pm-done-peek-open' : '',
        dragActive ? 'pm-done-peek-drag' : '',
        dragOverHere ? 'pm-done-peek-over' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={dragActive ? 'Drop here to mark project done' : 'Open done'}
      aria-expanded={open}
      onClick={onToggle}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      hidden={open}
    >
      <span className="pm-done-peek-icon">
        <IconCheckCircle size={14} />
      </span>
      <span className="pm-done-peek-label">
        {dragActive ? 'Drop to mark done' : 'Done'}
      </span>
      <span className="pm-done-peek-count">{count}</span>
      <span className="pm-done-peek-chev" aria-hidden="true">
        <IconChev size={12} />
      </span>
    </button>
    </div>,
    document.body,
  );
};

export default DonePeekTab;
